import { useState, useEffect, useCallback } from 'react';
import { Link } from '@tanstack/react-router';
import useScrolled from '../../hooks/useScrolled.js';
import Logo from '../ui/Logo.jsx';
import Button from '../ui/Button.jsx';
import { MenuIcon } from '../ui/icons.jsx';
import { navLinks } from '../../content/site.js';

/** Inline close (X) icon — no external dep required */
function CloseIcon({ className }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <line x1="4" y1="4" x2="20" y2="20" />
      <line x1="20" y1="4" x2="4" y2="20" />
    </svg>
  );
}

/**
 * Nav — fixed top bar. Permanently dark theme; gains glass background
 * once the page is scrolled past the threshold (see useScrolled).
 *
 * The "Talk to Merlin" CTA is a plain router link to /intake; pass
 * hideCta (RootLayout does this on /intake) to suppress it where the
 * visitor is already talking to Merlin.
 */
export default function Nav({ hideCta = false }) {
  const scrolled = useScrolled(12);
  const [mobileOpen, setMobileOpen] = useState(false);

  /** Close drawer on Escape key */
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && mobileOpen) {
      setMobileOpen(false);
    }
  }, [mobileOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /** Close drawer and smooth-scroll when a mobile nav link is clicked.
      Off the homepage the section id does not exist, so let the default
      navigation take the visitor to /#section instead. */
  function handleMobileLinkClick(e, href) {
    const id = href.split('#')[1] ?? '';
    const target = document.getElementById(id);
    if (!target) {
      setMobileOpen(false);
      return;
    }
    e.preventDefault();
    setMobileOpen(false);
    // Small delay lets the drawer start closing before scroll fires
    setTimeout(() => target.scrollIntoView({ behavior: 'smooth' }), 80);
  }

  return (
    <header
      className={
        'fixed top-0 left-0 right-0 z-50 text-ink-50 ' +
        'transition-[background-color,backdrop-filter,border-color] duration-200 border-b ' +
        (scrolled || mobileOpen
          ? 'bg-surface/90 backdrop-blur-glass border-white/[.06]'
          : 'bg-transparent border-transparent')
      }
    >
      {/* ── Top bar ── */}
      <div className="container-page flex items-center justify-between gap-8 py-4">
        {/* New logo lockup carries its own tagline — the Perth locator would be redundant here */}
        <Logo showLocator={false} />

        <nav aria-label="Primary" className="hidden lg:flex items-center gap-7 text-sm font-medium">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="opacity-85 hover:opacity-100 transition-opacity"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2.5">
          {!hideCta && (
            <Button variant="primary" as={Link} to="/intake">
              Talk to Merlin
            </Button>
          )}

          {/* Hamburger / close toggle — mobile only */}
          <button
            type="button"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-drawer"
            className="lg:hidden p-2 text-ink-50 transition-opacity hover:opacity-80"
          >
            {mobileOpen
              ? <CloseIcon className="w-5 h-5" />
              : <MenuIcon className="w-5 h-5" />
            }
          </button>
        </div>
      </div>

      {/* ── Mobile drawer ── */}
      <div
        id="mobile-nav-drawer"
        role="navigation"
        aria-label="Mobile navigation"
        aria-hidden={!mobileOpen}
        className={
          'lg:hidden overflow-hidden transition-[max-height,opacity] duration-300 ease-in-out ' +
          (mobileOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0')
        }
      >
        <ul className="container-page flex flex-col gap-1 pb-5 pt-1">
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                onClick={(e) => handleMobileLinkClick(e, link.href)}
                className={
                  'block py-3 px-2 text-sm font-medium rounded-md ' +
                  'text-ink-50 opacity-85 hover:opacity-100 ' +
                  'hover:bg-white/[.04] transition-[opacity,background-color] duration-150 ' +
                  'border-b border-white/[.06] last:border-0'
                }
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
}
